import React, { createContext } from 'react';
import styled from 'styled-components';
import { MantraAccordion, MantraAccordionPanel } from '../../../Components/Accordion';
import { Text } from '../../../globalStyles';
import { useCampusResourcesQuery, VideoCallSidebarQuery } from '../../../graphQL';
import { BEACON_RESOURCE_TYPES } from '../../Handbook/constants/beaconResourceMetadata';
import { CAMPUS_RESOURCE_TYPES } from '../../Handbook/constants/campusResourceMetadata';
import { getHandbookPanels, HandbookPanelKey } from '../../Handbook/panels';

type SafeOperatingHandbookProps = {
  data: VideoCallSidebarQuery;
  options?: SafeOperatingHandbookOptions;
};

type SafeOperatingHandbookOptions = {
  vertical?: boolean; // Controls flow direction for handbook layout
};

type SohFlowContextType = {
  vertical?: boolean;
};

export const SohFlowContext = createContext<SohFlowContextType | null>(null);

export const SafeOperatingHandbookTab = ({ data, options }: SafeOperatingHandbookProps) => {
  const { organization } = data.user;

  const { data: resourcesData, refetch: refetchResources } = useCampusResourcesQuery({
    variables: { organizationId: organization?.id ?? 0 },
    skip: !organization,
  });

  if (!organization?.safeOperatingHandbook) {
    return <></>;
  }

  const vertical = options?.vertical ?? false;

  const handbookPanels = getHandbookPanels({ excludePanels: [HandbookPanelKey.beaconResources] });

  const allResources = resourcesData?.organization?.resources ?? [];
  const campusResources = allResources.filter(resource =>
    CAMPUS_RESOURCE_TYPES.includes(resource.resourceType)
  );
  const beaconResources = allResources.filter(resource =>
    BEACON_RESOURCE_TYPES.includes(resource.resourceType)
  );

  return (
    <SohFlowContext.Provider value={{ vertical }}>
      <HandbookTabSection>
        <Text.h2 className="mb4">{organization.name}</Text.h2>
        <MantraAccordion multi>
          {handbookPanels.map((panel, idx) => (
            <MantraAccordionPanel
              key={`panel-${idx}-${panel.title}`}
              className="pb3"
              withBottomBorder
              title={
                <Text.bodyBold id={panel.title} className="mr4">
                  {panel.title}
                </Text.bodyBold>
              }
            >
              {panel.render({
                organizationId: organization.id,
                editable: false,
                verticalDisplay: true,
                handbookInfo: organization.safeOperatingHandbook,
                campusResources,
                beaconResources,
                refetchResources,
                selectedField: null,
              })}
            </MantraAccordionPanel>
          ))}
        </MantraAccordion>
      </HandbookTabSection>
    </SohFlowContext.Provider>
  );
};

const HandbookTabSection = styled.section`
  padding: 2rem 0.25rem;
`;
